import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/new';
import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/core/search';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { Flex } from '@atlaskit/primitives/compiled';

import type { ContentType } from './filters/TypeFilter.tsx';
import type { SpaceOption } from './filters/SpaceFilter.tsx';
import type { ContentOwnerOption } from './filters/ContentOwnerFilter.tsx';
import { TypeFilter } from './filters/TypeFilter.tsx';
import { RatingFilter } from './filters/RatingFilter.tsx';
import { SpaceFilter } from './filters/SpaceFilter.tsx';
import { ContentOwnerFilter } from './filters/ContentOwnerFilter.tsx';
import type { LoadTimeRating } from './ContentMetricsTable.tsx';

const i18n = defineMessages({
	searchPlaceholder: {
		id: 'performance-insights.content-metrics.view-controls.search-placeholder.non-final',
		defaultMessage: 'Search title',
		description:
			'Placeholder text for the search field above the content metrics table. Allows admins to filter the table by content title. Displayed on the Content metrics tab of the Performance analytics page.',
	},
	searchIconLabel: {
		id: 'performance-insights.content-metrics.view-controls.search-icon-label.non-final',
		defaultMessage: 'Search',
		description:
			'Accessible label for the search icon in the content metrics search field. Displayed on the Content metrics tab of the Performance analytics page.',
	},
	searchDescription: {
		id: 'performance-insights.content-metrics.view-controls.search-description.non-final',
		defaultMessage: 'Content will be filtered as you type',
		description:
			'Screen reader description for the search field. Informs assistive technology users that the table results update live as they type. Displayed on the Content metrics tab of the Performance analytics page.',
	},
	clearAll: {
		id: 'performance-insights.content-metrics.view-controls.clear-all.non-final',
		defaultMessage: 'Clear all',
		description:
			'Label for the button that resets all active filters and the search field in the content metrics table. Displayed on the Content metrics tab of the Performance analytics page.',
	},
});

const styles = cssMap({
	container: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	searchIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});

const SEARCH_DESCRIPTION_ID = 'content-metrics-search-description';

export type ViewControlsProps = {
	searchText: string;
	onSearchChange: (value: string) => void;
	selectedTypes: ContentType[];
	onTypeSelectionChange: (selectedTypes: ContentType[]) => void;
	selectedRatings: LoadTimeRating[];
	onRatingSelectionChange: (selectedRatings: LoadTimeRating[]) => void;
	spaces: SpaceOption[];
	selectedSpaceIds: string[];
	onSpaceSelectionChange: (selectedSpaceIds: string[]) => void;
	owners: ContentOwnerOption[];
	selectedOwnerIds: string[];
	onOwnerSelectionChange: (selectedOwnerIds: string[]) => void;
	onClearAll: () => void;
};

export const ViewControls = ({
	searchText,
	onSearchChange,
	selectedTypes,
	onTypeSelectionChange,
	selectedRatings,
	onRatingSelectionChange,
	spaces,
	selectedSpaceIds,
	onSpaceSelectionChange,
	owners,
	selectedOwnerIds,
	onOwnerSelectionChange,
	onClearAll,
}: ViewControlsProps) => {
	const { formatMessage } = useIntl();

	const handleSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
		onSearchChange(e.currentTarget.value);
	};

	return (
		<>
			<Flex xcss={styles.container}>
				<Textfield
					isCompact
					width="medium"
					aria-label={formatMessage(i18n.searchPlaceholder)}
					aria-describedby={SEARCH_DESCRIPTION_ID}
					placeholder={formatMessage(i18n.searchPlaceholder)}
					value={searchText}
					onChange={handleSearchChange}
					elemBeforeInput={
						<span aria-hidden="true" css={styles.searchIcon}>
							<SearchIcon spacing="spacious" label={formatMessage(i18n.searchIconLabel)} />
						</span>
					}
				/>
				<TypeFilter selectedTypes={selectedTypes} onSelectionChange={onTypeSelectionChange} />
				<RatingFilter
					selectedRatings={selectedRatings}
					onSelectionChange={onRatingSelectionChange}
				/>
				<SpaceFilter
					spaces={spaces}
					selectedSpaceIds={selectedSpaceIds}
					onSelectionChange={onSpaceSelectionChange}
				/>
				<ContentOwnerFilter
					owners={owners}
					selectedOwnerIds={selectedOwnerIds}
					onSelectionChange={onOwnerSelectionChange}
				/>
				<Button appearance="subtle" onClick={onClearAll}>
					{formatMessage(i18n.clearAll)}
				</Button>
			</Flex>
			<VisuallyHidden id={SEARCH_DESCRIPTION_ID}>
				{formatMessage(i18n.searchDescription)}
			</VisuallyHidden>
		</>
	);
};
